import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Tabs,
  Tab,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { AccountBalance, ArrowBack, CheckCircle, Cancel, Search, FilterList, Refresh, Policy, IosShare } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Employee, EmploymentStatus, LeaveRequest, LeaveRequestStatus, displayName } from '../../types';
import { apiService } from '../../services/api';
import { apiFetch } from '../../services/apiFetch';
import ManageLeaveBalanceModal from '../../components/ManageLeaveBalanceModal';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`leave-tabpanel-${index}`}
      aria-labelledby={`leave-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const EmployeeLeaveManagement: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [applyingPolicy, setApplyingPolicy] = useState(false);
  const [policySuccess, setPolicySuccess] = useState('');
  const [runningCarryOver, setRunningCarryOver] = useState(false);

  // Leave balances per employee
  const [allBalances, setAllBalances] = useState<Record<string, any[]>>({});
  const [balancesLoading, setBalancesLoading] = useState(false);

  // Leave requests state
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveError, setLeaveError] = useState('');
  const [leaveSuccess, setLeaveSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [leaveSearch, setLeaveSearch] = useState('');
  const [actionDialog, setActionDialog] = useState<{ open: boolean; request: any; action: 'approve' | 'reject' } | null>(null);
  const [actionNote, setActionNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [approverComments, setApproverComments] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<LeaveRequest | null>(null);

  const fetchLeaveRequests = useCallback(async () => {
    try {
      setLeaveLoading(true);
      const data = await apiService.getLeaveRequests();
      setLeaveRequests(data);
    } catch {
      setLeaveError('Failed to load leave requests');
    } finally {
      setLeaveLoading(false);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getEmployees();
      setEmployees(data);
    } catch (err: any) {
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBalances = useCallback(async (emps: Employee[]) => {
    if (emps.length === 0) return;
    setBalancesLoading(true);
    Promise.all(
      emps.map(emp =>
        apiService.getEmployeeLeaveBalances(emp.id).then(b => ({ id: emp.id, balances: b })).catch(() => ({ id: emp.id, balances: [] }))
      )
    ).then(results => {
      const map: Record<string, any[]> = {};
      results.forEach(r => { map[r.id] = r.balances; });
      setAllBalances(map);
    }).finally(() => setBalancesLoading(false));
  }, []);

  const refreshAll = useCallback(async () => {
    const data = await apiService.getEmployees().catch(() => [] as Employee[]);
    setEmployees(data);
    await fetchBalances(data);
    fetchLeaveRequests();
  }, [fetchBalances, fetchLeaveRequests]);

  useEffect(() => {
    fetchEmployees().then(() => {});
    fetchLeaveRequests();
  }, [fetchEmployees, fetchLeaveRequests]);

  // Fetch balances once employees are loaded
  useEffect(() => {
    fetchBalances(employees);
  }, [employees, fetchBalances]);

  const handleLeaveAction = async () => {
    if (!actionDialog) return;
    setActionLoading(true);
    try {
      const status = actionDialog.action === 'approve' ? 'approved' : 'rejected';
      await apiService.approveLeaveRequest(actionDialog.request.id, {
        status,
        approverComments: actionNote || undefined,
      });
      setLeaveSuccess(`Leave request ${actionDialog.action}d successfully`);
      setActionDialog(null);
      setActionNote('');
      fetchLeaveRequests();
      setTimeout(() => setLeaveSuccess(''), 3000);
    } catch {
      setLeaveError(`Failed to ${actionDialog.action} leave request`);
    } finally {
      setActionLoading(false);
    }
  };


  const handleManageLeave = (employee: Employee) => {
    setSelectedEmployee(employee);
    setIsLeaveModalOpen(true);
  };

  const handleYearEndCarryOver = async () => {
    if (!window.confirm('Run year-end carry-over for all employees? This will carry over up to 5 unused leave days into the next year, expiring 31 March.')) return;
    try {
      setRunningCarryOver(true);
      setError('');
      const result = await apiService.runYearEndCarryOver();
      setPolicySuccess(`Year-end carry-over complete — ${result.processed} leave balance${result.processed !== 1 ? 's' : ''} updated. Carry-over expires 31 March.`);
      await refreshAll();
      setTimeout(() => setPolicySuccess(''), 8000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to run year-end carry-over');
    } finally {
      setRunningCarryOver(false);
    }
  };

  const handleApplyPolicyToAll = async () => {
    try {
      setApplyingPolicy(true);
      setError('');
      const result = await apiService.applyLeavePolicy();
      setPolicySuccess(`Leave policy applied to ${result.updated} employee${result.updated !== 1 ? 's' : ''}. Annual leave updated based on tenure (14 days base + 1/year, max 20).`);
      await refreshAll();
      setTimeout(() => setPolicySuccess(''), 6000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to apply leave policy');
    } finally {
      setApplyingPolicy(false);
    }
  };

  const handleCloseLeaveModal = () => {
    setIsLeaveModalOpen(false);
    setSelectedEmployee(null);
  };

  const handleOpenAction = (request: LeaveRequest, type: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setActionType(type);
    setApproverComments('');
    setActionDialogOpen(true);
  };

  const handleCloseAction = () => {
    setActionDialogOpen(false);
    setSelectedRequest(null);
    setApproverComments('');
  };

  const handleSubmitAction = async () => {
    if (!selectedRequest) return;
    try {
      setActionLoading(true);
      await apiService.approveLeaveRequest(selectedRequest.id, {
        status: actionType === 'approve' ? LeaveRequestStatus.APPROVED : LeaveRequestStatus.REJECTED,
        approverComments: approverComments || undefined,
      });
      handleCloseAction();
      await fetchLeaveRequests();
    } catch (err: any) {
      console.error(`Error ${actionType}ing leave request:`, err);
      setError(`Failed to ${actionType} leave request`);
    } finally {
      setActionLoading(false);
    }
  };

  const pendingCount = leaveRequests.filter(r => r.status === LeaveRequestStatus.PENDING).length;

  const filteredRequests = leaveRequests.filter(r => {
    if (statusFilter === 'pending') return r.status === LeaveRequestStatus.PENDING;
    if (statusFilter === 'approved') return r.status === LeaveRequestStatus.APPROVED;
    if (statusFilter === 'rejected') return r.status === LeaveRequestStatus.REJECTED;
    if (statusFilter === 'cancelled') return r.status === LeaveRequestStatus.CANCELLED;
    return true;
  });

  const getLeaveStatusColor = (status: LeaveRequestStatus) => {
    switch (status) {
      case LeaveRequestStatus.APPROVED: return 'success';
      case LeaveRequestStatus.REJECTED: return 'error';
      case LeaveRequestStatus.PENDING: return 'warning';
      case LeaveRequestStatus.CANCELLED: return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status: EmploymentStatus) => {
    switch (status) {
      case EmploymentStatus.ACTIVE:
        return 'success';
      case EmploymentStatus.TERMINATED:
        return 'error';
      case EmploymentStatus.ON_LEAVE:
        return 'warning';
      default:
        return 'default';
    }
  };

  const filteredEmployees = employees.filter(
    (employee) =>
      employee.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (employee.nickname && employee.nickname.toLowerCase().includes(searchTerm.toLowerCase())) ||
      employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.department?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with back button */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton
          onClick={() => navigate('/employees')}
          sx={{ mr: 2 }}
          color="primary"
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ flex: 1 }}>Employee Leave Management</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<IosShare />}
          onClick={() => {
            apiFetch('/api/leave/calendar/team.ics')
              .then((r) => r.blob())
              .then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = 'team-leave.ics'; a.click();
                URL.revokeObjectURL(url);
              });
          }}
        >
          Export Team Calendar
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {employees.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Employees
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {employees.filter(e => e.employmentStatus === 'active').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Employees
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {employees.filter(e => e.employmentStatus === 'on_leave').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                On Leave
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color={pendingCount > 0 ? 'error.main' : 'info.main'}>
                {pendingCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Requests
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for different views */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Employee Leave Balances" />
          <Tab label="Leave Requests" />
          <Tab label="Leave Reports" />
        </Tabs>
        <Tooltip title="Refresh all data">
          <IconButton onClick={refreshAll} size="small" sx={{ mr: 1 }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Tab 1: Employee Leave Balances */}
      <TabPanel value={tabValue} index={0}>
        {policySuccess && <Alert severity="success" sx={{ mb: 2 }}>{policySuccess}</Alert>}

        {/* Leave Policy Info Banner */}
        <Alert severity="info" icon={<Policy />} sx={{ mb: 2 }}>
          <strong>Leave Policy:</strong> Annual Leave starts at 14 days. Every year you're with us, we add one more day — up to a maximum of 18 days. Sick Leave is a flat 5 days per year.
          <Box mt={1}>
            <Button
              size="small"
              variant="outlined"
              color="info"
              onClick={handleApplyPolicyToAll}
              disabled={applyingPolicy}
              startIcon={applyingPolicy ? <CircularProgress size={14} /> : <Policy />}
              sx={{ mr: 1 }}
            >
              {applyingPolicy ? 'Applying…' : 'Apply Policy to All'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={handleYearEndCarryOver}
              disabled={runningCarryOver}
              startIcon={runningCarryOver ? <CircularProgress size={14} /> : undefined}
              title="Run at year-end: carries over up to 5 unused days per employee, expiring 31 March"
            >
              {runningCarryOver ? 'Running…' : 'Run Year-End Carry-Over'}
            </Button>
          </Box>
        </Alert>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>🔍</Typography>,
              }}
            />
          </Grid>
        </Grid>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f9f9f9' }}>
                  <TableCell><strong>Employee</strong></TableCell>
                  <TableCell><strong>Department</strong></TableCell>
                  <TableCell align="center"><strong>Annual Leave</strong><br/><Typography variant="caption" color="text.secondary">Entitlement / Used / Remaining</Typography></TableCell>
                  <TableCell align="center"><strong>Sick Leave</strong><br/><Typography variant="caption" color="text.secondary">Entitlement / Used / Remaining</Typography></TableCell>
                  <TableCell align="center"><strong>Manage</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balancesLoading && (
                  <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={20} sx={{ my: 1 }} /></TableCell></TableRow>
                )}
                {filteredEmployees.map((employee) => {
                  const balances = allBalances[employee.id] || [];
                  const annual = balances.find(b => b.leaveType?.name?.toLowerCase().includes('annual'));
                  const sick = balances.find(b => b.leaveType?.name?.toLowerCase().includes('sick'));

                  const renderBalance = (bal: any) => {
                    if (!bal) return <Typography variant="caption" color="text.secondary">—</Typography>;
                    const entitlement = parseFloat(bal.openingBalanceDays || 0) + parseFloat(bal.accruedDays || 0) + parseFloat(bal.adjustedDays || 0);
                    const used = parseFloat(bal.usedDays || 0);
                    const remaining = parseFloat(bal.closingBalanceDays || 0);
                    const carryOver = parseFloat(bal.carryOverDays || 0);
                    return (
                      <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                        <Box display="flex" gap={1} justifyContent="center" alignItems="center">
                          <Typography variant="body2">{entitlement}d</Typography>
                          <Typography variant="caption" color="text.secondary">/</Typography>
                          <Typography variant="body2" color={used > 0 ? 'warning.main' : 'text.secondary'}>{used}d</Typography>
                          <Typography variant="caption" color="text.secondary">/</Typography>
                          <Typography variant="body2" fontWeight={700} color={remaining <= 2 ? 'error.main' : 'success.main'}>{remaining}d</Typography>
                        </Box>
                        {carryOver > 0 && (
                          <Chip label={`+${carryOver}d carried over`} size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                        )}
                      </Box>
                    );
                  };

                  return (
                    <TableRow key={employee.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{displayName(employee)}</Typography>
                        <Typography variant="caption" color="text.secondary">{employee.email}</Typography>
                      </TableCell>
                      <TableCell>{employee.department?.name || '-'}</TableCell>
                      <TableCell align="center">{renderBalance(annual)}</TableCell>
                      <TableCell align="center">{renderBalance(sick)}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleManageLeave(employee)} title="Manage Leave Balances" color="primary"><AccountBalance /></IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredEmployees.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">
                        {searchTerm ? 'No employees found matching your search.' : 'No employees found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? 's' : ''}
            {searchTerm && ` (filtered from ${employees.length})`}
          </Typography>
        </Box>
      </TabPanel>

      {/* Tab 2: Leave Requests */}
      <TabPanel value={tabValue} index={1}>
        {leaveSuccess && <Alert severity="success" sx={{ mb: 2 }}>{leaveSuccess}</Alert>}
        {leaveError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLeaveError('')}>{leaveError}</Alert>}

        {/* Filters */}
        <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            placeholder="Search employee..."
            value={leaveSearch}
            onChange={e => setLeaveSearch(e.target.value)}
            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} /> }}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} label="Status">
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="withdrawn">Withdrawn</MenuItem>
            </Select>
          </FormControl>
          <Chip
            label={`${leaveRequests.filter(r => r.status === 'pending').length} pending`}
            color="warning"
            size="small"
            icon={<FilterList />}
          />
        </Box>

        {leaveLoading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f9f9f9' }}>
                  <TableCell><strong>Employee</strong></TableCell>
                  <TableCell><strong>Leave Type</strong></TableCell>
                  <TableCell><strong>From</strong></TableCell>
                  <TableCell><strong>To</strong></TableCell>
                  <TableCell><strong>Days</strong></TableCell>
                  <TableCell><strong>Reason</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaveRequests
                  .filter(r => statusFilter === 'all' || r.status === statusFilter)
                  .filter(r => {
                    if (!leaveSearch) return true;
                    const name = `${r.employee?.firstName} ${r.employee?.lastName} ${r.employee?.nickname || ''}`.toLowerCase();
                    return name.includes(leaveSearch.toLowerCase());
                  })
                  .sort((a, b) => {
                    // Pending first, then by date
                    if (a.status === 'pending' && b.status !== 'pending') return -1;
                    if (b.status === 'pending' && a.status !== 'pending') return 1;
                    return new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime();
                  })
                  .map(req => {
                    const start = new Date(req.startDate);
                    const end = new Date(req.endDate);
                    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                    const isPending = req.status === 'pending';
                    return (
                      <TableRow key={req.id} sx={{ '&:hover': { bgcolor: '#fafafa' }, bgcolor: isPending ? '#fffbf5' : 'inherit' }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {displayName(req.employee)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{req.employee?.email}</Typography>
                        </TableCell>
                        <TableCell>{req.leaveType?.name || '—'}</TableCell>
                        <TableCell>{start.toLocaleDateString()}</TableCell>
                        <TableCell>{end.toLocaleDateString()}</TableCell>
                        <TableCell>{days}d</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {req.reason || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                            size="small"
                            color={req.status === 'approved' ? 'success' : req.status === 'rejected' ? 'error' : req.status === 'pending' ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {isPending && (
                            <Box display="flex" gap={0.5} justifyContent="center">
                              <Tooltip title="Approve">
                                <IconButton size="small" color="success" onClick={() => { setActionDialog({ open: true, request: req, action: 'approve' }); setActionNote(''); }}>
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton size="small" color="error" onClick={() => { setActionDialog({ open: true, request: req, action: 'reject' }); setActionNote(''); }}>
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                          {!isPending && <Typography variant="caption" color="text.secondary">—</Typography>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {leaveRequests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No leave requests found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Approve / Reject Dialog */}
        <Dialog open={!!actionDialog?.open} onClose={() => setActionDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {actionDialog?.action === 'approve' ? '✅ Approve Leave Request' : '❌ Reject Leave Request'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" mb={2}>
              <strong>{displayName(actionDialog?.request?.employee)}</strong>
              {' — '}{actionDialog?.request?.leaveType?.name}
              {' · '}{actionDialog?.request?.startDate && new Date(actionDialog.request.startDate).toLocaleDateString()}
              {' – '}{actionDialog?.request?.endDate && new Date(actionDialog.request.endDate).toLocaleDateString()}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Note (optional)"
              value={actionNote}
              onChange={e => setActionNote(e.target.value)}
              placeholder={actionDialog?.action === 'reject' ? 'Reason for rejection...' : 'Any notes for the employee...'}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActionDialog(null)} disabled={actionLoading}>Cancel</Button>
            <Button
              variant="contained"
              color={actionDialog?.action === 'approve' ? 'success' : 'error'}
              onClick={handleLeaveAction}
              disabled={actionLoading}
            >
              {actionLoading ? <CircularProgress size={18} /> : (actionDialog?.action === 'approve' ? 'Approve' : 'Reject')}
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* Tab 3: Leave Reports */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6">Leave Reports &amp; Analytics</Typography>
              <Chip label="Coming soon" size="small" color="warning" variant="outlined" />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Not yet available — leave usage reports, trends, and analytics are planned but
              not built yet. Use the Leave Balance Report page for current org-wide balances.
            </Typography>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Leave Balance Management Modal */}
      <ManageLeaveBalanceModal
        open={isLeaveModalOpen}
        onClose={handleCloseLeaveModal}
        employee={selectedEmployee}
      />

      {/* Approve/Reject Dialog */}
      <Dialog open={actionDialogOpen} onClose={handleCloseAction} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve' ? 'Approve' : 'Reject'} Leave Request
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Box sx={{ mb: 2, mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2"><strong>Employee:</strong> {selectedRequest.employee?.fullName || '-'}</Typography>
                <Typography variant="body2"><strong>Leave Type:</strong> {selectedRequest.leaveType?.name || '-'}</Typography>
                <Typography variant="body2">
                  <strong>Period:</strong> {new Date(selectedRequest.startDate).toLocaleDateString()} - {new Date(selectedRequest.endDate).toLocaleDateString()} ({selectedRequest.totalDays} day{selectedRequest.totalDays !== 1 ? 's' : ''})
                </Typography>
                {selectedRequest.reason && (
                  <Typography variant="body2"><strong>Reason:</strong> {selectedRequest.reason}</Typography>
                )}
              </Box>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={actionType === 'reject' ? 'Reason for Rejection' : 'Comments (optional)'}
                required={actionType === 'reject'}
                value={approverComments}
                onChange={(e) => setApproverComments(e.target.value)}
                placeholder={actionType === 'reject' ? 'Please provide a reason for rejection...' : 'Add any comments...'}
                error={actionType === 'reject' && approverComments.trim() === ''}
                helperText={actionType === 'reject' && approverComments.trim() === '' ? 'A reason is required when rejecting' : ''}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAction} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitAction}
            variant="contained"
            color={actionType === 'approve' ? 'success' : 'error'}
            disabled={actionLoading || (actionType === 'reject' && approverComments.trim() === '')}
          >
            {actionLoading ? <CircularProgress size={20} /> : actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeLeaveManagement;